import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Typography } from 'ui/typography';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import ActionMenu from 'components/partials/action-menu/action-menu';
import { Loader } from 'ui/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import { useInsightDetails } from 'containers/user/insights/insight-detail/hooks/use-insight-details';
import { useInsightComments } from 'containers/user/insights/insight-detail/hooks/use-insight-comments';
import { useExportPdf } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/hooks/use-export-pdf';
import InsightDetailsTab from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/tabs/insight-details-tab';
import PdfContent from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/pdf-content';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

const InsightDetail = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const currentUser = useCurrentUser();
  const { insight, loading, error, markInsightDownloaded } = useInsightDetails({
    insightId: id,
  });

  const { comments, loading: commentsLoading } = useInsightComments({
    insightId: id,
  });

  const { exportToPdf, isExporting } = useExportPdf();

  const userId = currentUser?.data?.oktaUserId;
  const currentUserId = currentUser?.data?.id;

  if (loading) {
    return <Loader loading={true} />;
  }

  if (error || !insight) {
    return (
      <ResponsiveWrapper>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <p className="text-red-500">Error loading insight details</p>
        </Box>
      </ResponsiveWrapper>
    );
  }

  const breadcrumbs = [
    { label: 'Insights', href: '/insights' },
    { label: insight.name ?? 'Insight', isActive: true },
  ];

  const handleDownloadPdf = async (): Promise<void> => {
    try {
      await exportToPdf({
        insightName: insight.name ?? 'Insight',
        contentRef: pdfContentRef,
      });

      if (userId && currentUserId && id && !insight.downloaded) {
        await markInsightDownloaded();
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
    }
  };

  return (
    <ResponsiveWrapper>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mt: 3,
          mb: 2.5,
          columnGap: 4,
        }}
      >
        <Box>
          <Typography
            data-testid="insight-heading"
            variant="h1"
            sx={{ mb: 0.75 }}
          >
            {insight.name}
          </Typography>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              color: 'tokens.gray.700',
              mb: 2,
              columnGap: 1.5,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
              <Icons.Calendar className="size-4" />
              <Box
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  fontWeight: 600,
                }}
              >
                {formatDate(insight.createdDate, 'MMM D, YYYY')}
              </Box>
            </Stack>
            <Box>
              <span className="text-gray-600 text-sm">Created by: </span>
              <span className="text-gray-700 text-sm font-semibold">
                {insight.createdBy || 'Unknown'}
              </span>
            </Box>
          </Stack>
        </Box>

        <ActionMenu
          buttonName="Download PDF"
          onEditClick={handleDownloadPdf}
          iconBefore={<Icons.Download />}
          isActionMenuDisabled={isExporting}
          loading={isExporting}
        />
      </Stack>

      <InsightDetailsTab
        insight={insight}
        comments={comments}
        commentsLoading={commentsLoading}
        isAdminView={false}
      />

      {/* Hidden PDF content for download */}
      <Box
        sx={{
          position: 'fixed',
          top: '-9999px',
          left: '-9999px',
          opacity: 0,
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        <PdfContent
          ref={pdfContentRef}
          insight={insight}
          comments={comments}
          showViewReport={false}
        />
      </Box>
    </ResponsiveWrapper>
  );
};

export default InsightDetail;
